import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { colorSet } from '@components/Provider';

interface useDragAnimationProps {
  headerRef: React.MutableRefObject<HTMLDivElement | null>;
  titleWrapperRef: React.MutableRefObject<HTMLDivElement | null>;
  closeButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
  subButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
  pannelRef: React.MutableRefObject<HTMLDivElement | null>;
  hasBackgroundImage?: boolean;
}

const useDragAnimation = (props: useDragAnimationProps) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const [initializing, setInitializing] = useState<boolean>(false);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [move, setMove] = useState<'top' | 'bottom' | null>(null);
  const scrollPosition = useRef<number>(0);
  const scrollTimeout = useRef<number | null>(null);
  const draggableContainerRef = useRef<HTMLDivElement | null>(null);
  const draggableContentRef = useRef<HTMLDivElement | null>(null);
  const draggableBottomNavRef = useRef<HTMLDivElement | null>(null);

  const {
    headerRef,
    titleWrapperRef,
    closeButtonRef,
    subButtonRef,
    pannelRef,
    hasBackgroundImage
  } = props;

  const initializeStyle = useCallback(() => {
    setInitializing(true);
  }, []);

  const dragAnimation = useCallback(() => {
    if (!matches) return;
    const ratio = matches ? scrollPosition.current / 160 : 0;
    const colorValue = 255 - ratio * 255;

    if (pannelRef.current) {
      pannelRef.current.style.borderRadius = `${16 - 16 * ratio}px`;
      pannelRef.current.style.paddingTop =
        ratio >= 1 ? '60px' : `${ratio * 60}px`;
    }

    if (headerRef.current) {
      headerRef.current.style.backgroundColor =
        ratio >= 1 ? colorSet.gray000 : 'transparent';
      headerRef.current.style.height = ratio >= 1 ? '60px' : '59px';
      headerRef.current.style.borderBottom =
        ratio >= 1 ? `1px solid ${colorSet.gray400}` : 'none';
    }

    if (titleWrapperRef.current) {
      titleWrapperRef.current.style.backgroundColor = hasBackgroundImage
        ? `rgba(0, 0, 0, ${0.5 + 0.5 * ratio})`
        : `rgba(0, 0, 0, ${ratio})`;
      titleWrapperRef.current.style.color = `rgba(255, 255, 255, ${1 - ratio})`;
    }

    if (closeButtonRef.current) {
      closeButtonRef.current.style.backgroundColor = `rgba(${colorValue}, ${colorValue}, ${colorValue}, ${
        0.2 - ratio * 0.2
      }`;
      closeButtonRef.current.style.color = `rgb(${colorValue}, ${colorValue}, ${colorValue})`;
    }

    if (subButtonRef.current) {
      subButtonRef.current.style.backgroundColor =
        ratio >= 1
          ? colorSet.gray100
          : `rgba(${colorValue}, ${colorValue}, ${colorValue}, ${
              0.2 - ratio * 0.2
            }`;
      subButtonRef.current.style.color = `rgb(${colorValue}, ${colorValue}, ${colorValue})`;
    }
  }, [
    matches,
    headerRef,
    pannelRef,
    titleWrapperRef,
    closeButtonRef,
    subButtonRef,
    hasBackgroundImage
  ]);

  const magnetMove = useCallback(() => {
    if (!matches) return;

    if (move === 'top') {
      window.scroll({
        top: 160,
        left: 0,
        behavior: 'smooth'
      });

      setMove(null);
    }

    if (move === 'bottom') {
      window.scroll({
        top: 1,
        left: 0,
        behavior: 'smooth'
      });

      setMove(null);
    }
  }, [move, matches]);

  const scrollEvent = useCallback(() => {
    if (!isScrolling) {
      setIsScrolling(true);
    }

    if (scrollTimeout.current) {
      window.clearTimeout(scrollTimeout.current);
      scrollTimeout.current = null;
    }

    if (!scrollTimeout.current) {
      const timeout = window.setTimeout(() => {
        setIsScrolling(false);
        scrollTimeout.current = null;
      }, 500);
      scrollTimeout.current = timeout;
    }

    scrollPosition.current = window.scrollY;
    dragAnimation();
  }, [isScrolling, dragAnimation]);

  // get Elements
  useEffect(() => {
    window.addEventListener('scroll', scrollEvent);

    return () => window.removeEventListener('scroll', () => scrollEvent);
  }, [scrollEvent]);

  useEffect(() => {
    if (!matches) return;

    if (!isScrolling && scrollPosition.current <= 160) {
      if (scrollPosition.current < 60) {
        setMove('bottom');
      } else {
        setMove('top');
      }
    }
  }, [isScrolling, matches]);

  useEffect(() => {
    const subButton = document.querySelector('.subButton');
    if (subButton) {
      subButtonRef.current = subButton as HTMLButtonElement;
    }
  }, [subButtonRef]);

  useEffect(() => {
    const draggableContainerElement = document.querySelector(
      '.draggable-conatiner'
    );
    if (draggableContainerElement) {
      draggableContainerRef.current = draggableContainerElement as HTMLDivElement;
    }
  }, [draggableContainerRef]);

  useEffect(() => {
    const pannelContent = document.querySelector('.draggable-content');
    if (pannelContent) {
      draggableContentRef.current = pannelContent as HTMLDivElement;
    }
  }, [draggableContentRef]);

  useEffect(() => {
    const pannelBottomNav = document.querySelector('.draggable-bottomNav');
    if (draggableBottomNavRef) {
      draggableBottomNavRef.current = pannelBottomNav as HTMLDivElement;
    }
  });

  useEffect(() => {
    if (move) {
      magnetMove();
    }
  }, [move, magnetMove]);

  // style initialize.
  useEffect(() => {
    if (!matches || initializing) {
      window.scrollTo({ top: 0, left: 0 });

      if (pannelRef.current) {
        pannelRef.current.style.borderRadius = '';
        pannelRef.current.style.paddingTop = '';
      }

      if (headerRef.current) {
        headerRef.current.style.backgroundColor = '';
        headerRef.current.style.borderBottom = '';
      }

      if (titleWrapperRef.current) {
        titleWrapperRef.current.style.backgroundColor = '';
        titleWrapperRef.current.style.color = '';
      }

      if (closeButtonRef.current) {
        closeButtonRef.current.style.backgroundColor = '';
        closeButtonRef.current.style.color = '';
      }

      if (subButtonRef.current) {
        subButtonRef.current.style.backgroundColor = '';
        subButtonRef.current.style.color = '';
      }
    }

    setInitializing(false);
    //eslint-disable-next-line
  }, [matches, initializing]);

  return {
    setMove,
    initializeStyle
  };
};

export default useDragAnimation;
